// Inspection table columns — the domain cells per the design: reference-id
// chip, name tile + description sub-line, form/project chips, company-structure
// cell (level icon + node/level lines), priority/status chips, tags with "+N"
// overflow, and the row-actions kebab. Start date and Created by exist as
// extra columns (hidden by default via useColumnVisibility on the page).
//
// The pack is shared: the inspections page passes the kebab callbacks, while the
// project detail page's read-only Inspections tab calls it with no arguments and
// gets the same cells WITHOUT an actions column. Its cell styles live in the
// top-level `.inspection-table` block (inspection-page.scss), not nested under
// `.inspection-page`, so they apply wherever the pack renders.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon, type DataTableColumn } from '@/components/common';
import {
  InspectionSortKey,
  type InspectionListItem,
  type InspectionPerson,
} from '@/api/endpoints/inspectionApi';
import InspectionRowActions from './InspectionRowActions';
import InspectionAssigneesCell from './InspectionAssigneesCell';
import { PRIORITY_META, STATE_META, structureLines } from './inspectionMeta';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import { fmtDate } from '@/utils/formatDate';

const dash = <span className='inspection-table__dash'>—</span>;

const PersonCell = ({ person }: { person: InspectionPerson | null }) => {
  if (!person) return dash;
  const fullName = `${person.firstName} ${person.lastName}`.trim();
  if (!fullName) return dash;
  return (
    <span className='inspection-table__person'>
      <Avatar initials={initialsOf(fullName)} size={28} tone={toneOf(person.id || fullName)} />
      <span className='inspection-table__person-name'>{fullName}</span>
    </span>
  );
};

interface InspectionColumnsOptions {
  /** Archived tab — the kebab drops the lifecycle verbs and offers Delete
   *  instead (see InspectionRowActions for the full menu rule). */
  archivedTab?: boolean;
  /** Marks the confirmed-orderBy columns sortable (keyed by InspectionSortKey).
   *  Only the inspections page passes this — the read-only project tab wires no
   *  onSort, and DataTable draws a glyph for any sortable column regardless, so
   *  leaving it off there keeps dead chevrons out. */
  sortable?: boolean;
  /** Row-kebab actions. AT LEAST ONE must be passed for the actions column to
   *  render at all, and each item is hidden unless its own callback is present
   *  — the same hide-don't-disable rule the project row actions follow. This is
   *  what lets a READ-ONLY surface (the project detail page's Inspections tab)
   *  reuse this pack without a kebab it can't honour. */
  onEdit?: (inspection: InspectionListItem) => void;
  onDuplicate?: (inspection: InspectionListItem) => void;
  onSuspendResume?: (inspection: InspectionListItem) => void;
  onArchive?: (inspection: InspectionListItem) => void;
  onDelete?: (inspection: InspectionListItem) => void;
}

export const useInspectionColumns = ({
  archivedTab,
  sortable,
  onEdit,
  onDuplicate,
  onSuspendResume,
  onArchive,
  onDelete,
}: InspectionColumnsOptions = {}): DataTableColumn<InspectionListItem>[] => {
  const { t } = useTranslation();

  const columns: DataTableColumn<InspectionListItem>[] = [
    {
      key: InspectionSortKey.REFERENCE_NUMBER,
      label: t('inspection_col_id'),
      width: '7rem',
      sortable,
      render: (i) => (
        <span className='inspection-table__id'>
          INSP-{String(i.referenceNumber).padStart(2, '0')}
        </span>
      ),
    },
    {
      key: InspectionSortKey.TITLE,
      label: t('inspection_col_title'),
      width: 'minmax(16rem, 1.6fr)',
      sortable,
      render: (i) => (
        <span className='inspection-table__name-cell'>
          <span className='inspection-table__tile'>
            <Icon name='clipboard-list' size={18} />
          </span>
          <span className='inspection-table__label'>
            <span className='inspection-table__name'>{i.title || '—'}</span>
            {i.description && <span className='inspection-table__sub'>{i.description}</span>}
          </span>
        </span>
      ),
    },
    {
      key: 'form',
      label: t('inspection_col_form'),
      render: (i) =>
        i.form?.title ? (
          <Chip variant='outline' size='lg' iconStart={i.form.icon}>
            {i.form.title}
          </Chip>
        ) : (
          dash
        ),
    },
    {
      key: 'project',
      label: t('inspection_col_project'),
      render: (i) =>
        i.project?.title ? (
          <Chip variant='outline' size='lg' iconStart={i.project.icon}>
            {i.project.title}
          </Chip>
        ) : (
          dash
        ),
    },
    {
      key: 'structure',
      label: t('inspection_col_structure'),
      render: (i) => {
        const lines = structureLines(i, t);
        if (!lines) return dash;
        return (
          <span className='inspection-table__structure'>
            <Icon name={lines.icon} size={16} className='inspection-table__structure-icon' />
            <span className='inspection-table__structure-label'>
              <span className='inspection-table__structure-name'>{lines.name}</span>
              <span className='inspection-table__structure-sub'>{lines.sub}</span>
            </span>
          </span>
        );
      },
    },
    {
      key: InspectionSortKey.PRIORITY,
      label: t('inspection_col_priority'),
      sortable,
      render: (i) => {
        if (!i.priority) return dash;
        const priority = PRIORITY_META[i.priority];
        return (
          <Chip variant={priority.chip} iconStart={priority.icon}>
            {t(`inspection_priority_${i.priority}`)}
          </Chip>
        );
      },
    },
    {
      key: 'tags',
      label: t('inspection_col_tags'),
      render: (i) => {
        const shown = i.tags.slice(0, 2);
        const extra = i.tags.length - shown.length;
        return (
          <span className='inspection-table__tags'>
            {i.tags.length === 0 ? (
              dash
            ) : (
              <>
                {shown.map((tag) => (
                  <Chip key={tag.id} variant='neutral' iconStart='tag'>
                    {tag.title}
                  </Chip>
                ))}
                {extra > 0 && <span className='inspection-table__more'>+{extra}</span>}
              </>
            )}
          </span>
        );
      },
    },
    {
      key: InspectionSortKey.STATE,
      label: t('inspection_col_state'),
      sortable,
      render: (i) => {
        const state = STATE_META[i.state];
        return (
          <Chip variant={state.chip} iconStart={state.icon}>
            {t(`inspection_state_${i.state}`)}
          </Chip>
        );
      },
    },
    {
      key: 'assignees',
      label: t('inspection_field_assigned'),
      render: (i) => <InspectionAssigneesCell inspection={i} />,
    },
    {
      // The cell renders start→end; the backend sorts the pair by start date.
      key: InspectionSortKey.START_DATE,
      label: t('inspection_col_dates'),
      sortable,
      render: (i) =>
        !i.startDate && !i.endDate ? (
          dash
        ) : (
          <span className='inspection-table__dates'>
            {fmtDate(i.startDate)}
            <Icon name='arrow-right' size={14} className='inspection-table__dates-arrow' />
            {fmtDate(i.endDate)}
          </span>
        ),
    },
    {
      key: 'createdBy',
      label: t('inspection_col_created_by'),
      render: (i) => <PersonCell person={i.createdBy} />,
    },
  ];

  // Any one callback earns the column; the row actions decide which ITEMS show.
  // A caller that passes none (the read-only project tab) gets no kebab at all.
  if (onEdit || onDuplicate || onSuspendResume || onArchive || onDelete)
    columns.push({
      key: 'actions',
      label: t('inspection_col_actions'),
      align: 'end',
      width: '5rem',
      render: (i) => (
        <InspectionRowActions
          state={i.state}
          archivedTab={archivedTab}
          onEdit={onEdit && (() => onEdit(i))}
          onDuplicate={onDuplicate && (() => onDuplicate(i))}
          onSuspendResume={onSuspendResume && (() => onSuspendResume(i))}
          onArchive={onArchive && (() => onArchive(i))}
          onDelete={onDelete && (() => onDelete(i))}
        />
      ),
    });

  return columns;
};
